import type { ContentTypeManifest } from './manifest';
import { invoiceContentType } from './invoice';
import { receiptContentType } from './receipt';
import { Data, Effect } from 'effect';

/**
 * Registry of built-in content types (W.4.24d.2 / #331). The discovery surface
 * (`okra content-types list` / `show`) and, later, the generated `okra <noun>
 * extract` commands read from here. Keyed by `id` (== the generated CLI noun
 * unless `cli.noun` overrides). Today this is the single canonical `invoice`;
 * the codegen ticket extends it.
 */
const BUILTIN_CONTENT_TYPES = [invoiceContentType, receiptContentType] as const;

/** A compact, list-friendly view of a content type (no schema/fields dump). */
export interface ContentTypeSummary {
  id: string;
  label: string;
  version: string;
  cli_noun: string;
  sdk_namespace: string;
  fields: number;
  citation_required: boolean;
}

function summarize(m: ContentTypeManifest): ContentTypeSummary {
  return {
    id: m.id,
    label: m.label,
    version: m.version,
    cli_noun: m.cli.noun,
    sdk_namespace: m.sdk.namespace,
    fields: m.fields.length,
    citation_required: m.evidence.citation_required,
  };
}

export class ContentTypeRegistryError extends Data.TaggedError('ContentTypeRegistryError')<{
  readonly lookup: string;
  readonly existingId: string;
  readonly conflictingId: string;
}> {}

export interface ContentTypeRegistry {
  readonly manifests: readonly ContentTypeManifest[];
  list(): ContentTypeSummary[];
  get(idOrNoun: string): ContentTypeManifest | undefined;
}

/** Build a lookup registry and fail if ids, nouns, or aliases collide. */
export function createContentTypeRegistryEffect(
  manifests: readonly ContentTypeManifest[],
): Effect.Effect<ContentTypeRegistry, ContentTypeRegistryError> {
  return Effect.suspend(() => {
    const byLookup = new Map<string, ContentTypeManifest>();
    for (const manifest of manifests) {
      const lookups = [manifest.id, manifest.cli.noun, ...(manifest.cli.aliases ?? [])];
      for (const rawLookup of lookups) {
        const lookup = rawLookup.trim().toLowerCase();
        const existing = byLookup.get(lookup);
        if (existing && existing !== manifest) {
          return Effect.fail(
            new ContentTypeRegistryError({
              lookup,
              existingId: existing.id,
              conflictingId: manifest.id,
            }),
          );
        }
        byLookup.set(lookup, manifest);
      }
    }

    const ordered = [...manifests].sort((a, b) => a.id.localeCompare(b.id));
    return Effect.succeed({
      manifests: ordered,
      list: () => ordered.map(summarize),
      get: (idOrNoun) => byLookup.get(idOrNoun.trim().toLowerCase()),
    });
  });
}

const BUILTIN_CONTENT_TYPE_REGISTRY = Effect.runSync(
  createContentTypeRegistryEffect(BUILTIN_CONTENT_TYPES),
);

/** All registered content types as a native Effect program. */
export function listContentTypesEffect(): Effect.Effect<ContentTypeSummary[]> {
  return Effect.sync(() => BUILTIN_CONTENT_TYPE_REGISTRY.list());
}

/** All registered content types as compact summaries (stable id order). */
export function listContentTypes(): ContentTypeSummary[] {
  return Effect.runSync(listContentTypesEffect());
}

/** Resolve a content type by id/noun/alias as a native Effect program. */
export function getContentTypeEffect(
  idOrNoun: string,
): Effect.Effect<ContentTypeManifest | undefined> {
  return Effect.sync(() => BUILTIN_CONTENT_TYPE_REGISTRY.get(idOrNoun));
}

/** Resolve a content type by id (or its CLI noun / aliases). Undefined if unknown. */
export function getContentType(idOrNoun: string): ContentTypeManifest | undefined {
  return Effect.runSync(getContentTypeEffect(idOrNoun));
}
